import React, { forwardRef, useEffect, useState } from 'react';
import type { AvatarRootState } from '@base-ui/react/avatar';
import { Avatar as BaseAvatar } from '@base-ui/react/avatar';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';
import cn from '~/Utils/cn';

interface AvatarBaseProps {
  /**
   * Shown when there is no `src`, when the image fails to load, and while an image is still
   * loading after `FALLBACK_DELAY_MS`: initials, a letter, or an icon.
   */
  children?: React.ReactNode;
  /** Applied to the root. Size and background utilities replace the 40px and azure defaults. */
  className?: string;
  /** The image URL. An empty string counts as no image. */
  src?: string;
}

/** Renders a `<span>`. */
interface StaticAvatarProps extends AvatarBaseProps {
  onClick?: never;
  'aria-label'?: never;
  'aria-expanded'?: never;
  'aria-haspopup'?: never;
  'aria-controls'?: never;
}

/** Renders a `<button>`. The image is decorative, so the button needs its own accessible name. */
interface ButtonAvatarProps extends AvatarBaseProps {
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  'aria-label': string;
  /** For an avatar that opens a menu or a popover. */
  'aria-expanded'?: boolean;
  /** For an avatar that opens a popover: `usePopoverState` returns these with `aria-expanded` as `triggerProps`. */
  'aria-haspopup'?: 'dialog';
  'aria-controls'?: string;
}

export type AvatarProps = StaticAvatarProps | ButtonAvatarProps;

/**
 * How long a loading image keeps the fallback hidden. A photo that arrives sooner never flashes
 * the initials first; a slower one shows them until it arrives, so the circle is never empty for
 * longer than this. A failed or missing image shows the fallback at once.
 */
export const FALLBACK_DELAY_MS = 600;

// MUI's Avatar geometry and typography, which every consumer was built against. The text colour is
// the Figma initials variant's white.
const rootClasses =
  'flex h-40 w-40 shrink-0 select-none items-center justify-center overflow-hidden rounded-full text-[20px] leading-none text-white';

// The Figma initials background, painted until a photo shows. Some stored photos are PNGs, which
// can be transparent, so the default must not show through one. It stays on while a photo loads,
// because a slow load shows the white initials after the delay.
const defaultBackgroundClass = 'bg-azure-700';

// `button-reset` (index.css) clears the browser's visible button styles at zero specificity.
const buttonClasses = 'button-reset cursor-pointer';

const Avatar = forwardRef<HTMLElement, AvatarProps>(
  (
    {
      children,
      className,
      src,
      onClick,
      'aria-label': ariaLabel,
      'aria-expanded': ariaExpanded,
      'aria-haspopup': ariaHasPopup,
      'aria-controls': ariaControls,
      ...restProps
    },
    ref
  ) => {
    if (!isEmpty(restProps)) {
      reportErrorInProductionOrThrow(
        `Unexpected props passed to Avatar: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the AvatarProps interface and to the component's signature.`,
        true
      );
    }

    // The union requires the label for typed callers. This covers untyped (JS) callers, such as
    // CmsDrawer, where an unlabelled button would otherwise pass silently.
    if (onClick && !ariaLabel) {
      reportErrorInProductionOrThrow(
        'Avatar has an onClick but no aria-label. A clickable avatar renders a button, and its image is decorative, so the label is its only accessible name.',
        true
      );
    }

    // Keyed by the source it timed, so a new `src` starts hidden again. The effect clears it on every
    // change, so a return to an earlier `src` does not find that source's old entry.
    const [delayElapsedForSrc, setDelayElapsedForSrc] = useState<string>();
    useEffect(() => {
      setDelayElapsedForSrc(undefined);
      if (!src) {
        return undefined;
      }
      const timer = setTimeout(() => setDelayElapsedForSrc(src), FALLBACK_DELAY_MS);
      return () => clearTimeout(timer);
    }, [src]);

    // Base UI reports `idle` for the moment before it starts loading, so anything short of an
    // error counts as loading here.
    const isFallbackDelayed = (state: AvatarRootState) =>
      Boolean(src) && state.imageLoadingStatus !== 'error' && delayElapsedForSrc !== src;

    return (
      <BaseAvatar.Root
        data-fs-component="avatar"
        ref={ref}
        render={
          onClick ? (
            <button
              type="button"
              onClick={onClick}
              aria-label={ariaLabel}
              aria-expanded={ariaExpanded}
              aria-haspopup={ariaHasPopup}
              aria-controls={ariaControls}
            />
          ) : undefined
        }
        className={(state) =>
          cn(
            rootClasses,
            onClick && buttonClasses,
            state.imageLoadingStatus !== 'loaded' && defaultBackgroundClass,
            className
          )
        }
      >
        {src && <BaseAvatar.Image src={src} alt="" className="h-full w-full object-cover" />}
        {/* `contents` drops the fallback's own box, so its children lay out in the root exactly as
          they did as MUI's direct children. A delayed fallback carries the `hidden` attribute
          instead: any author rule, `contents` included, would beat the browser's [hidden] rule. */}
        <BaseAvatar.Fallback
          render={(props, state) => (
            <span
              {...props}
              hidden={isFallbackDelayed(state)}
              className={isFallbackDelayed(state) ? undefined : 'contents'}
            />
          )}
        >
          {children}
        </BaseAvatar.Fallback>
      </BaseAvatar.Root>
    );
  }
);

Avatar.displayName = 'Avatar';

export default Avatar;
